import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link as RouterLink, useLocation, useNavigate, useParams } from 'react-router-dom';
import {
  Avatar,
  Box,
  Button,
  Card,
  Chip,
  CircularProgress,
  Divider,
  IconButton,
  Link,
  Tooltip,
  Typography,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import FormatQuoteIcon from '@mui/icons-material/FormatQuote';
import LockIcon from '@mui/icons-material/Lock';
import LockOpenIcon from '@mui/icons-material/LockOpen';
import NotificationsIcon from '@mui/icons-material/Notifications';
import NotificationsOffIcon from '@mui/icons-material/NotificationsOff';
import PushPinIcon from '@mui/icons-material/PushPin';
import SaveIcon from '@mui/icons-material/Save';
import CloseIcon from '@mui/icons-material/Close';
import AppBreadcrumbs from '../components/common/AppBreadcrumbs';
import ConfirmDialog from '../components/common/ConfirmDialog';
import DiskussionPostBody from '../components/diskussion/DiskussionPostBody';
import DiskussionPostReactions from '../components/diskussion/DiskussionPostReactions';
import DiskussionRichEditor from '../components/diskussion/DiskussionRichEditor';
import { buildQuoteHtml } from '../components/diskussion/diskussionQuote';
import type { TipTapEditorHandle } from '../components/common/TipTapEditor';
import { useAuth } from '../hooks/useAuth';
import { usePermissions } from '../hooks/usePermissions';
import { diskussionService } from '../services/diskussionService';
import type { DiskussionPost, DiskussionReactionSummary, DiskussionThreadDetail } from '../types';
import * as P from '../constants/permissions';

const isEmptyHtml = (s: string): boolean => {
  const t = s.replace(/<p>\s*<\/p>|<br\s*\/?>|&nbsp;|\s/g, '').trim();
  return t === '' || t === '<p></p>';
};

function authorName(autor: { vorname: string; nachname: string } | null): string {
  return autor ? `${autor.vorname} ${autor.nachname}` : 'Unbekannt';
}

function authorInitials(name: string): string {
  const parts = name.split(/\s+/).filter(Boolean);
  const letters = parts.slice(0, 2).map((part) => part[0]?.toUpperCase() ?? '');
  return letters.join('') || '?';
}

const DiskussionThreadPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const location = useLocation();
  const navigate = useNavigate();
  const threadId = Number(id);
  const { user } = useAuth();
  const { hasPermission } = usePermissions();
  const [thread, setThread] = useState<DiskussionThreadDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [reply, setReply] = useState('');
  const [sending, setSending] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [editBody, setEditBody] = useState('');
  const [savingEdit, setSavingEdit] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<DiskussionPost | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [watchToggling, setWatchToggling] = useState(false);
  const [moderating, setModerating] = useState(false);
  const replyEditorRef = useRef<TipTapEditorHandle | null>(null);
  const replySectionRef = useRef<HTMLDivElement | null>(null);

  const canModerate = hasPermission(P.DISKUSSION_MODERATE) || hasPermission(P.VERWALTUNG_MANAGE);

  const loadThread = useCallback(async () => {
    if (!Number.isFinite(threadId)) return;
    const data = await diskussionService.getThread(threadId);
    setThread(data);
  }, [threadId]);

  useEffect(() => {
    let c = false;
    (async () => {
      if (!Number.isFinite(threadId)) return;
      setLoading(true);
      try {
        const data = await diskussionService.getThread(threadId);
        if (!c) setThread(data);
      } finally {
        if (!c) setLoading(false);
      }
    })();
    return () => {
      c = true;
    };
  }, [threadId]);

  useEffect(() => {
    if (!thread || !location.hash) return;
    const targetId = decodeURIComponent(location.hash.slice(1));
    if (!targetId) return;

    window.requestAnimationFrame(() => {
      document.getElementById(targetId)?.scrollIntoView({ block: 'start' });
    });
  }, [thread, location.hash]);

  const sendReply = async () => {
    if (isEmptyHtml(reply) || !thread) return;
    setSending(true);
    try {
      await diskussionService.createPost(thread.id, reply);
      setReply('');
      await loadThread();
    } finally {
      setSending(false);
    }
  };

  const startEdit = (p: DiskussionPost) => {
    setEditingId(p.id);
    setEditBody(p.body);
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEditBody('');
  };

  const saveEdit = async () => {
    if (!thread || editingId == null || isEmptyHtml(editBody)) return;
    setSavingEdit(true);
    try {
      await diskussionService.updatePost(thread.id, editingId, editBody);
      cancelEdit();
      await loadThread();
    } finally {
      setSavingEdit(false);
    }
  };

  const confirmDelete = async () => {
    if (!thread || !deleteTarget) return;
    setDeleting(true);
    try {
      await diskussionService.deletePost(thread.id, deleteTarget.id);
      setDeleteTarget(null);
      try {
        await loadThread();
      } catch {
        navigate(`/diskussion/kategorie/${thread.kategorie_id}`, { replace: true });
      }
    } finally {
      setDeleting(false);
    }
  };

  const canModifyPost = (p: DiskussionPost) => {
    if (!hasPermission(P.DISKUSSION_POST) || !user) return false;
    const own = p.autor?.id === user.id;
    if (!own && !canModerate) return false;
    if (thread?.locked && !canModerate) return false;
    return true;
  };

  const canQuotePost = () =>
    hasPermission(P.DISKUSSION_POST) && thread !== null && !thread.locked;

  const updatePostReactions = useCallback((postId: number, reactions: DiskussionReactionSummary[]) => {
    setThread((prev) => {
      if (!prev) return prev;
      return {
        ...prev,
        posts: prev.posts.map((p) => (p.id === postId ? { ...p, reactions } : p)),
      };
    });
  }, []);

  const toggleWatch = async () => {
    if (!thread || watchToggling) return;
    setWatchToggling(true);
    try {
      if (thread.watching) {
        await diskussionService.unwatchThread(thread.id);
        setThread({ ...thread, watching: false });
      } else {
        await diskussionService.watchThread(thread.id);
        setThread({ ...thread, watching: true });
      }
    } finally {
      setWatchToggling(false);
    }
  };

  const patchThreadFlags = async (data: { locked?: boolean; pinned?: boolean }) => {
    if (!thread || moderating) return;
    setModerating(true);
    try {
      const updated = await diskussionService.patchThread(thread.id, data);
      setThread(updated);
    } finally {
      setModerating(false);
    }
  };

  const quotePost = (p: DiskussionPost) => {
    const html = buildQuoteHtml(p);
    if (replyEditorRef.current) {
      replyEditorRef.current.insertText(html);
    } else {
      setReply((prev) => (prev || '') + html);
    }
    replySectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
  };

  if (loading || !thread) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ minWidth: 0 }}>
      <AppBreadcrumbs
        items={[
          { label: 'Diskussion', path: '/diskussion' },
          {
            label: thread.kategorie_name?.trim() || 'Kategorie',
            path: `/diskussion/kategorie/${thread.kategorie_id}`,
          },
          { label: thread.titel },
        ]}
      />
      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, mb: 1, flexWrap: 'wrap', minWidth: 0 }}>
        <Box sx={{ flex: '1 1 220px', minWidth: 0 }}>
          <Typography variant="h5" component="h1" sx={{ overflowWrap: 'anywhere' }}>
            {thread.titel}
          </Typography>
          <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap', mt: 0.75 }}>
            {thread.pinned && <Chip size="small" color="primary" icon={<PushPinIcon />} label="Angeheftet" />}
            <Typography variant="body2" color="text.secondary" sx={{ alignSelf: 'center' }}>
              {thread.posts.length === 1 ? '1 Beitrag' : `${thread.posts.length} Beiträge`}
              {' · '}
              {authorName(thread.autor)}
            </Typography>
          </Box>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', flexShrink: 0 }}>
          {canModerate && (
            <>
              <Tooltip title={thread.pinned ? 'Nicht mehr anheften' : 'Thread anheften'}>
                <span>
                  <IconButton
                    onClick={() => patchThreadFlags({ pinned: !thread.pinned })}
                    disabled={moderating}
                    color={thread.pinned ? 'primary' : 'default'}
                    aria-label={thread.pinned ? 'Nicht mehr anheften' : 'Thread anheften'}
                  >
                    <PushPinIcon />
                  </IconButton>
                </span>
              </Tooltip>
              <Tooltip title={thread.locked ? 'Thread entsperren' : 'Thread sperren'}>
                <span>
                  <IconButton
                    onClick={() => patchThreadFlags({ locked: !thread.locked })}
                    disabled={moderating}
                    color={thread.locked ? 'warning' : 'default'}
                    aria-label={thread.locked ? 'Thread entsperren' : 'Thread sperren'}
                  >
                    {thread.locked ? <LockIcon /> : <LockOpenIcon />}
                  </IconButton>
                </span>
              </Tooltip>
            </>
          )}
          <Tooltip title={thread.watching ? 'Thread nicht mehr beobachten' : 'Thread beobachten (Benachrichtigungen)'}>
            <span>
              <IconButton
                onClick={toggleWatch}
                disabled={watchToggling}
                color={thread.watching ? 'primary' : 'default'}
                aria-label={thread.watching ? 'Beobachtung beenden' : 'Thread beobachten'}
              >
                {watchToggling ? (
                  <CircularProgress size={22} />
                ) : thread.watching ? (
                  <NotificationsIcon />
                ) : (
                  <NotificationsOffIcon />
                )}
              </IconButton>
            </span>
          </Tooltip>
        </Box>
      </Box>
      {thread.anker.length > 0 && (
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 2 }}>
          {thread.anker.map((a, i) => (
            <Chip
              key={`${a.ref_type}-${a.ref_id}-${i}`}
              component={RouterLink}
              to={a.path}
              label={a.label}
              clickable
              size="small"
              variant="outlined"
            />
          ))}
        </Box>
      )}
      {thread.locked && (
        <Typography color="warning.main" sx={{ mb: 2 }}>
          Dieser Thread ist gesperrt.
          {canModerate && ' Als Moderator/in oder Admin können Sie Beiträge dennoch bearbeiten oder löschen.'}
        </Typography>
      )}

      {thread.posts.map((p, idx) => {
        const name = authorName(p.autor);
        return (
          <Card key={p.id} id={`post-${p.id}`} variant="outlined" sx={{ mb: 2, scrollMarginTop: 96, overflow: 'hidden', minWidth: 0 }}>
            <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, minWidth: 0 }}>
              <Box
                sx={{
                  width: { xs: '100%', sm: 168 },
                  flexShrink: 0,
                  p: 2,
                  display: 'flex',
                  flexDirection: { xs: 'row', sm: 'column' },
                  alignItems: { xs: 'center', sm: 'flex-start' },
                  gap: 1.25,
                  bgcolor: (t) => (t.palette.mode === 'dark' ? 'rgba(255,255,255,0.03)' : 'rgba(0,0,0,0.03)'),
                  borderRight: { sm: 1 },
                  borderBottom: { xs: 1, sm: 0 },
                  borderColor: 'divider',
                  minWidth: 0,
                }}
              >
                <Avatar sx={{ width: 40, height: 40, bgcolor: 'primary.main', fontSize: '0.9rem', flexShrink: 0 }}>
                  {authorInitials(name)}
                </Avatar>
                <Box sx={{ minWidth: 0 }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 700, overflowWrap: 'anywhere' }}>
                    {name}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    #{idx + 1}
                  </Typography>
                </Box>
              </Box>
              <Box sx={{ flex: 1, minWidth: 0, p: 2 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 1, minWidth: 0 }}>
                  <Typography variant="caption" color="text.secondary" sx={{ flex: 1, minWidth: 0, overflowWrap: 'anywhere' }}>
                    {new Date(p.erstellt_am).toLocaleString('de-DE')}
                    {p.bearbeitet_am && (
                      <> · bearbeitet {new Date(p.bearbeitet_am).toLocaleString('de-DE')}</>
                    )}
                  </Typography>
                  {(canQuotePost() || (canModifyPost(p) && editingId !== p.id)) && (
                    <Box sx={{ display: 'flex', gap: 0.25, flexShrink: 0 }}>
                      {canQuotePost() && editingId !== p.id && (
                        <Tooltip title="Zitieren">
                          <IconButton size="small" onClick={() => quotePost(p)} aria-label="Beitrag zitieren">
                            <FormatQuoteIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                      {canModifyPost(p) && editingId !== p.id && (
                        <>
                          <Tooltip title="Bearbeiten">
                            <IconButton size="small" onClick={() => startEdit(p)} aria-label="Beitrag bearbeiten">
                              <EditIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Löschen">
                            <IconButton
                              size="small"
                              color="error"
                              onClick={() => setDeleteTarget(p)}
                              aria-label="Beitrag löschen"
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </>
                      )}
                    </Box>
                  )}
                </Box>

                {editingId === p.id ? (
                  <Box sx={{ mt: 1, minWidth: 0 }}>
                    <DiskussionRichEditor
                      value={editBody}
                      onChange={setEditBody}
                      disabled={savingEdit}
                      label="Beitrag bearbeiten"
                      allowChecklist={idx === 0 || /\[\[aio:checklist:[\w-]+\]\]/.test(p.body)}
                    />
                    <Box sx={{ mt: 1, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                      <Button
                        size="small"
                        variant="contained"
                        startIcon={savingEdit ? <CircularProgress size={16} /> : <SaveIcon />}
                        onClick={saveEdit}
                        disabled={savingEdit || isEmptyHtml(editBody)}
                      >
                        Speichern
                      </Button>
                      <Button size="small" startIcon={<CloseIcon />} onClick={cancelEdit} disabled={savingEdit}>
                        Abbrechen
                      </Button>
                    </Box>
                  </Box>
                ) : (
                  <>
                    <DiskussionPostBody
                      body={p.body}
                      refs={p.refs}
                      readOnlyChecklist={thread.locked && !canModerate}
                    />
                    {p.refs.some((r) => r.ref_type !== 'diskussion_datei') && (
                      <Box sx={{ mt: 1, display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                        {p.refs
                          .filter((r) => r.ref_type !== 'diskussion_datei')
                          .map((r, j) => (
                            <Link key={j} component={RouterLink} to={r.path} variant="caption" sx={{ overflowWrap: 'anywhere' }}>
                              {r.label}
                            </Link>
                          ))}
                      </Box>
                    )}
                    <DiskussionPostReactions
                      postId={p.id}
                      reactions={p.reactions ?? []}
                      disabled={!hasPermission(P.DISKUSSION_POST)}
                      onReactionsChange={(reactions) => updatePostReactions(p.id, reactions)}
                    />
                  </>
                )}
              </Box>
            </Box>
          </Card>
        );
      })}

      <Divider sx={{ my: 2 }} />

      {hasPermission(P.DISKUSSION_POST) && !thread.locked && (
        <Box ref={replySectionRef} sx={{ minWidth: 0 }}>
          <DiskussionRichEditor
            ref={replyEditorRef}
            value={reply}
            onChange={setReply}
            disabled={sending}
            label="Antwort"
            placeholder="Antwort verfassen… @m… erwähnt Kolleg:innen; Bilder per Strg+V einfügen"
          />
          <Button sx={{ mt: 1 }} variant="contained" onClick={sendReply} disabled={sending || isEmptyHtml(reply)}>
            {sending ? <CircularProgress size={22} /> : 'Senden'}
          </Button>
        </Box>
      )}

      {hasPermission(P.DISKUSSION_POST) && thread.locked && canModerate && (
        <Typography variant="body2" color="text.secondary">
          Thread gesperrt – neue Antworten sind deaktiviert. Sie können bestehende Beiträge bearbeiten oder löschen.
        </Typography>
      )}

      <ConfirmDialog
        open={deleteTarget !== null}
        title="Beitrag löschen?"
        message={
          deleteTarget && thread.posts.length <= 1 && canModerate
            ? 'Das ist der einzige Beitrag. Der gesamte Thread wird gelöscht.'
            : 'Diesen Beitrag wirklich löschen?'
        }
        onConfirm={confirmDelete}
        onCancel={() => !deleting && setDeleteTarget(null)}
        confirmText={deleting ? '…' : 'Löschen'}
      />
    </Box>
  );
};

export default DiskussionThreadPage;
